import React, { useEffect } from "react";
import {
    LoaderFunctionArgs,
    Navigate,
    Outlet,
    ScrollRestoration,
    createBrowserRouter,
    useLocation,
} from "react-router";
import { Spin } from "antd";

import ScreenLayout from "@/components/Layout";
import { usePageTracking } from "@/utils/analytics/googleAnalytics";
import AuthenticatedRoute from "./AuthenticatedRoute";
import { useTrialGPTFeatureFlag } from "@/utils/featureFlags";
import PublicOnlyRoute from "./PublicRoutes";

import Homepage from "./Home";
import { RedirectProvider } from "@/contexts/RedirectContext";
import { sendMessage } from "@/utils/iframeUtils";

function lazyRoute<
    TModule extends {
        default: React.ComponentType<unknown>;
        loader?: ({ params }: LoaderFunctionArgs) => Promise<unknown>;
        action?: () => unknown;
        ErrorBoundary?: React.ComponentType<unknown>;
        HydrateFallback?: React.ComponentType<unknown>;
    },
>(importer: () => Promise<TModule>) {
    return async () => {
        const module = await importer();
        return {
            Component: module.default,
            loader: module.loader,
            action: module.action,
            ErrorBoundary: module.ErrorBoundary,
            HydrateFallback: module.HydrateFallback || null,
        };
    };
}

function RootLayout() {
    const location = useLocation();
    usePageTracking();

    useEffect(() => {
        sendMessage("page_changed");
    }, [location]);

    return (
        <RedirectProvider>
            <ScrollRestoration />
            <Outlet />
        </RedirectProvider>
    );
}

function TrialGPTRoute() {
    const { isEnabled, isLoading } = useTrialGPTFeatureFlag();

    if (isLoading) {
        return <Spin size="large" />;
    }

    if (!isEnabled) {
        return <Navigate to="/" />;
    }

    return (
        <React.Suspense fallback={<Spin size="large" />}>
            {React.createElement(React.lazy(() => import("./TrialGPT")))}
        </React.Suspense>
    );
}

const router = createBrowserRouter([
    {
        path: "/",
        Component: RootLayout,
        children: [
            { path: "access_denied", lazy: lazyRoute(() => import("./AccessDenied")) },
            { path: "user_testing/sign_in", lazy: lazyRoute(() => import("./TestUserSignIn")) },
            { path: "beta/sign_in", lazy: lazyRoute(() => import("./BetaUser/BetaUserSignIn")) },
            { path: "beta/portal", lazy: lazyRoute(() => import("./BetaUser/BetaUserPortal")) },
            { path: "deleted", lazy: lazyRoute(() => import("./PostDelete")) },
            {
                Component: ScreenLayout,
                children: [
                    { path: "study/:nctID", lazy: lazyRoute(() => import("@/views/Study")) },
                    {
                        element: <PublicOnlyRoute defaultRedirect="/" />,
                        children: [
                            { path: "sign_up", lazy: lazyRoute(() => import("@/views/SignUp")) },
                            { path: "sign_in", lazy: lazyRoute(() => import("@/views/SignIn")) },
                        ],
                    },
                    { path: "research_readiness", lazy: lazyRoute(() => import("@/views/ResearchReadiness")) },
                    { path: "benefits", lazy: lazyRoute(() => import("@/views/WhyJoin")) },
                    { path: "overview", lazy: lazyRoute(() => import("@/views/LearnMore")) },
                    { path: "exploring", lazy: lazyRoute(() => import("@/views/LearnMore/Exploring")) },
                    { path: "empowering", lazy: lazyRoute(() => import("@/views/LearnMore/Empowering")) },
                    { path: "unlocking", lazy: lazyRoute(() => import("@/views/LearnMore/Unlocking")) },
                    { path: "glossary", lazy: lazyRoute(() => import("@/views/LearnMore/Glossary")) },
                    { path: "about", lazy: lazyRoute(() => import("@/views/About")) },
                    { path: "faq", lazy: lazyRoute(() => import("@/views/FAQ")) },
                    { path: "contact_us", lazy: lazyRoute(() => import("@/views/ContactUs")) },
                    { index: true, Component: Homepage },
                    { path: "privacy_policy", lazy: lazyRoute(() => import("@/views/PrivacyPolicy")) },
                    { path: "terms_of_use", lazy: lazyRoute(() => import("@/views/TermsOfUse")) },
                    { path: "new_alert", lazy: lazyRoute(() => import("./StudySearch")) },
                    {
                        element: <AuthenticatedRoute allowAnonymous />,
                        children: [{ path: "results", lazy: lazyRoute(() => import("@/views/Results")) }],
                    },
                    {
                        element: <AuthenticatedRoute />,
                        children: [
                            { path: "trialGPT", Component: TrialGPTRoute },
                            {
                                path: "reauthenticate",
                                lazy: lazyRoute(() => import("./Reauthenticate")),
                            },
                            {
                                path: "complete_signup",
                                lazy: lazyRoute(() => import("@/views/CompleteSignUp")),
                            },
                            {
                                path: "account",
                                lazy: lazyRoute(() => import("./Dashboard/indexV2")),
                                children: [
                                    { path: "*", element: <Navigate to="/account" /> },
                                    {
                                        path: "details",
                                        lazy: async () => {
                                            const module = await import("./Dashboard/EditForm");
                                            return {
                                                Component: (props: object) => (
                                                    <module.default isLoading={false} {...props} />
                                                ),
                                            };
                                        },
                                    },
                                    { path: "alerts", lazy: lazyRoute(() => import("./Dashboard/SearchAlerts")) },
                                    {
                                        path: "saved",
                                        lazy: lazyRoute(() => import("@/views/Dashboard/Studies/savedStudy")),
                                    },
                                    {
                                        path: "recommended",
                                        lazy: lazyRoute(() => import("./Dashboard/Studies/recommendedStudy")),
                                    },
                                    {
                                        path: "actions",
                                        lazy: lazyRoute(() => import("./Dashboard/PendingActions")),
                                    },
                                    {
                                        index: true,
                                        lazy: lazyRoute(() => import("./Dashboard/Studies/recommendedStudy")),
                                    },
                                    {
                                        path: "contacted",
                                        lazy: lazyRoute(() => import("@/views/Dashboard/Studies/contactedStudy")),
                                    },
                                    {
                                        path: "enrolled",
                                        lazy: lazyRoute(() => import("./Dashboard/Studies/enrolledStudies")),
                                    },
                                    { path: "delete", lazy: lazyRoute(() => import("./Dashboard/DeleteAccount")) },
                                ],
                            },
                        ],
                    },
                ],
            },
            { path: "*", element: <Navigate to="/" /> },
        ],
    },
]);

export default router;
